// SHARED-DES (docs/design/2026-10-30-shared-des-and-fulfillment.md §2.2, §3.1): a shared seat's
// serves links — the teams, seats or company it serves beside its one home line, each with the
// talk / task / see grants for the members of the served scope.
//
//   GET seats/{id}/serves   every chart viewer (the links draw the linked cards)
//   PUT seats/{id}/serves   owners and admins, `If-Match: "{version}"` of the set read (428
//                           without, 409 with the current set when it changed since): the
//                           whole link set (an omitted link is removed);
//                           `dry_run` returns the review lines and writes nothing (the drawer's
//                           review-and-apply). A write emits `seat.serves_changed`, then the
//                           STRUCTURE-CHANGED record `structure.changed` (kind serves).
//
// Agents never edit a link (a structure change is a person's).
import { z } from "zod";
import { SeatServesTargetKind } from "../enums";
import { Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

export { seatServesSetVersion } from "../../seat-serves-set";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** What the members of a served scope may do with the shared DE. */
export const SeatServesGrants = z
  .strictObject({ talk: z.boolean(), task: z.boolean(), see: z.boolean() })
  .meta({ id: "SeatServesGrants" });
export type SeatServesGrants = z.infer<typeof SeatServesGrants>;

export const ALL_GRANTS: SeatServesGrants = { talk: true, task: true, see: true };

/** One link of a shared seat (`target_id` null: the company). */
export const SeatServesLink = z
  .object({
    id: Uuid,
    seat_id: Uuid,
    target_kind: SeatServesTargetKind,
    target_id: Uuid.nullable(),
    /**
     * The served team's or seat's name (null: the company, a gone target, or a team the reader
     * may not see: the chart's team rule).
     */
    target_name: z.string().nullable(),
    grants: SeatServesGrants,
    version: z.int().min(1),
  })
  .meta({ id: "SeatServesLink" });
export type SeatServesLink = z.infer<typeof SeatServesLink>;

export const SeatServesView = z
  .object({
    seat_id: Uuid,
    links: z.array(SeatServesLink),
    /** The caller may change the links (an owner or admin). */
    editable: z.boolean(),
    /**
     * SHARED-DES-2: the set's version (`seatServesSetVersion`, also the `ETag`): `PUT` sends it
     * back as `If-Match`, so a stale drawer gets 409 instead of overwriting a newer change.
     */
    version: z.int().min(0),
  })
  .meta({ id: "SeatServesView" });
export type SeatServesView = z.infer<typeof SeatServesView>;

export const SeatServesLinkInput = z
  .strictObject({
    target_kind: SeatServesTargetKind,
    target_id: Uuid.nullable(),
    /** Absent: talk + task + see (the default for a served team). */
    grants: SeatServesGrants.optional(),
  })
  .refine((l) => (l.target_kind === "company") === (l.target_id === null), {
    message: "the company has no id; a seat or team needs one",
    path: ["target_id"],
  })
  .meta({ id: "SeatServesLinkInput" });
export type SeatServesLinkInput = z.infer<typeof SeatServesLinkInput>;

export const SeatServesPut = z
  .strictObject({
    links: z.array(SeatServesLinkInput).max(50),
    /** Why (kept on the `seat.serves_changed` record). */
    reason: z.string().trim().max(500).optional(),
    /** Review only: the change lines, nothing written. */
    dry_run: z.boolean().optional(),
  })
  .meta({ id: "SeatServesPut" });
export type SeatServesPut = z.infer<typeof SeatServesPut>;

/** One line of the review ("Account Health now serves Sales: talk, task, see"). */
export const SeatServesChange = z
  .object({
    op: z.enum(["add", "remove", "regrant"]),
    target_kind: SeatServesTargetKind,
    target_id: Uuid.nullable(),
    target_name: z.string().nullable(),
    grants: SeatServesGrants,
    say: z.string(),
  })
  .meta({ id: "SeatServesChange" });
export type SeatServesChange = z.infer<typeof SeatServesChange>;

export const SeatServesResult = SeatServesView.extend({
  changes: z.array(SeatServesChange),
  /** False for a dry run (or when nothing changed). */
  applied: z.boolean(),
}).meta({ id: "SeatServesResult" });
export type SeatServesResult = z.infer<typeof SeatServesResult>;

export const seatServesRoutes = {
  "seats.serves": defineRoute({
    ...core,
    id: "seats.serves",
    method: "get",
    path: `${base}/seats/{id}/serves`,
    tag: "org",
    summary: "A shared seat's serves links (teams, seats or the company) with their grants",
    description:
      "Every chart viewer of the company. A link is never a reporting line: the home seat keeps ownership, budget, KPIs and the chain.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatServesView },
    errors: [],
    emits: [],
  }),
  "seats.servesUpdate": defineRoute({
    ...core,
    id: "seats.servesUpdate",
    method: "put",
    path: `${base}/seats/{id}/serves`,
    tag: "org",
    summary: "Set a shared seat's serves links and grants (owners and admins; dry_run reviews)",
    description:
      "`If-Match` carries the set's `version` (428 without it, 409 with the current set when it changed since; a dry run checks it only when sent). The body is the whole link set: an omitted link is removed. Targets must be this company's open seats and teams (422); a seat never serves itself. `dry_run` returns the review lines and writes nothing. A change emits `seat.serves_changed` and, in the same transaction, `structure.changed` (kind `serves`): the seat's DEs are told what they serve now and the leaders get a notice. Talk on a team link puts the seat's DEs in the team's channel; losing it takes out the ones it put there unless something else keeps them (`channel.member_changed`). A served team's name is null for a reader who may not see that team.",
    access: "member",
    params: CompanyItemParams,
    body: SeatServesPut,
    ifMatch: true,
    response: { status: 200, schema: SeatServesResult },
    errors: [],
    emits: [
      "seat.serves_changed",
      "structure.changed",
      "inbox_item.created",
      "channel.member_changed",
    ],
  }),
} as const;
